/**
 * The one input record the simulation consumes.
 *
 * ==========================================================================
 *  WHY THIS TYPE EXISTS
 * ==========================================================================
 *  A run is only verifiable if the whole thing can be replayed from what the
 *  player actually did. That means every input the simulation reads has to be
 *  (a) sampled per fixed step, and (b) exactly representable in a few bytes.
 *
 *  Buttons are trivially exact. Mouse look is not: `yaw` is a float that a
 *  browser accumulates from raw pixel deltas, and storing it as a float64 would
 *  cost 16 bytes per step for precision no human can perceive.
 *
 *  So look angles are *quantised* - and, crucially, the live game feeds the
 *  simulation the quantised value too. The number the player is steered by and
 *  the number written into the replay are the same number, so a re-simulation
 *  is bit-identical rather than merely close.
 * ==========================================================================
 */

import { LOOK } from '../config/gameplay';
import { clamp } from '../utils/math';

/**
 * Button bits.
 *
 * The first seven are the hands: five for the legs, and F and E for the two
 * things a hand can do. The three after them are the boxes at the muurikka,
 * and they are the reason a frame's buttons no longer fit in the seven bits a
 * frame tag has room for - see `FAST_BUTTON_MASK`.
 */
export const BUTTON = {
  forward: 1 << 0,
  back: 1 << 1,
  left: 1 << 2,
  right: 1 << 3,
  jump: 1 << 4,
  /**
   * F, edge-triggered: set on the single step the player reached for
   * something. One bit for every interactable in the level - see
   * world/Interactable.ts for why there is only one.
   */
  action: 1 << 5,
  /** E, the same way: a pull off the bottle, and nothing else. */
  sip: 1 << 6,
  /**
   * `1`, `2` and `3`: which of the three boxes at the muurikka Kimmo grabbed
   * at. Edge-triggered like E and F, and deliberately three separate bits
   * rather than a two-bit number - a frame that says "box 2" and a frame that
   * says "no box" must not differ by a single flipped bit in a format whose
   * whole job is to be re-simulated byte for byte.
   *
   * Nothing outside `Aamupala` reads them, and it reads them through
   * `pickFromButtons`, so which key means which box is a fact about this file
   * and about the keyboard - not about breakfast.
   */
  pick1: 1 << 7,
  pick2: 1 << 8,
  pick3: 1 << 9,
} as const;

/**
 * Everything a legal frame may set. The replay decoder rejects anything else.
 */
export const PIN_KEY_MASK = 0x3c00;
export const BUTTON_MASK = 0x3fff;

/**
 * What fits in a frame tag byte, which is the fast path in the replay format:
 * seven bits, because the eighth is spoken for by the 0x80 "repeat" tag.
 *
 * A frame with any bit above this is written with the escape tag instead. That
 * costs a byte and happens at most a handful of times in a run - three key
 * presses if breakfast goes perfectly - so the common frame is exactly as
 * small as it was before the boxes existed. See ReplayFormat.
 */
export const FAST_BUTTON_MASK = 0x7f;

/** The three boxes, as one mask. */
export const PICK_MASK = BUTTON.pick1 | BUTTON.pick2 | BUTTON.pick3;

/** The edge-triggered bits. None of them may ever be run-length coded. */
export const EVENT_MASK = BUTTON.action | BUTTON.sip | PICK_MASK | PIN_KEY_MASK;

/** One mouse click per step: 1..11 encode digits 0..9 and backspace. */
export function buttonForPin(key: number): number {
  return Number.isInteger(key) && key >= 0 && key <= 10 ? (key + 1) << 10 : 0;
}

export function pinFromButtons(buttons: number): number {
  const key = ((buttons & PIN_KEY_MASK) >>> 10) - 1;
  return key >= 0 && key <= 10 ? key : -1;
}

/** The movement bits only - `action` is an event, not a held direction. */
export const MOVEMENT_MASK = BUTTON.forward | BUTTON.back | BUTTON.left | BUTTON.right | BUTTON.jump;

export interface StepInput {
  /** Bitmask of BUTTON.* */
  buttons: number;
  /** Yaw in quantised units, wrapped to a single turn. */
  yawQ: number;
  /** Pitch in quantised units, clamped to the legal look range. */
  pitchQ: number;
}

/* ------------------------------------------------------ look quantisation -- */

/**
 * 65536 steps to a full turn = 0.0055 degrees per unit.
 *
 * At the default sensitivity one mouse pixel is 0.0022 rad, which is ~23 units,
 * so the grid is roughly 1/23 of the smallest movement a mouse can report. It
 * is imperceptible in play and it makes a look delta fit in one or two bytes.
 */
export const LOOK_UNITS_PER_TURN = 65536;
export const LOOK_QUANTUM = (Math.PI * 2) / LOOK_UNITS_PER_TURN;

/** Pitch is clamped, so its unit range is fixed and small. */
export const PITCH_MIN_UNITS = Math.ceil(LOOK.minPitch / LOOK_QUANTUM);
export const PITCH_MAX_UNITS = Math.floor(LOOK.maxPitch / LOOK_QUANTUM);

/**
 * `Math.round(-0.2)` is -0, and -0 encodes and decodes as 0. Two frames that
 * differ only by the sign of zero are the same frame, so collapse it here
 * rather than letting a replay fail to compare equal to itself.
 */
function withoutNegativeZero(value: number): number {
  return value === 0 ? 0 : value;
}

/** Wraps yaw units into [-32768, 32767] so the delta encoding stays small. */
export function wrapYawUnits(units: number): number {
  const half = LOOK_UNITS_PER_TURN / 2;
  let wrapped = Math.round(units) % LOOK_UNITS_PER_TURN;
  if (wrapped >= half) wrapped -= LOOK_UNITS_PER_TURN;
  else if (wrapped < -half) wrapped += LOOK_UNITS_PER_TURN;
  return withoutNegativeZero(wrapped);
}

export function clampPitchUnits(units: number): number {
  return withoutNegativeZero(clamp(Math.round(units), PITCH_MIN_UNITS, PITCH_MAX_UNITS));
}

export function quantiseYaw(radians: number): number {
  return wrapYawUnits(Math.round(radians / LOOK_QUANTUM));
}

export function quantisePitch(radians: number): number {
  return clampPitchUnits(Math.round(radians / LOOK_QUANTUM));
}

/** The inverse. Both the live game and the verifier steer by *this* value. */
export function angleFromUnits(units: number): number {
  return units * LOOK_QUANTUM;
}

/* ------------------------------------------------------------- helpers ---- */

export function movementFromButtons(buttons: number): {
  forward: boolean;
  back: boolean;
  left: boolean;
  right: boolean;
  jump: boolean;
} {
  return {
    forward: (buttons & BUTTON.forward) !== 0,
    back: (buttons & BUTTON.back) !== 0,
    left: (buttons & BUTTON.left) !== 0,
    right: (buttons & BUTTON.right) !== 0,
    jump: (buttons & BUTTON.jump) !== 0,
  };
}

export function buttonsFromMovement(
  movement: { forward: boolean; back: boolean; left: boolean; right: boolean; jump: boolean },
  action = false,
  sip = false,
  /** Which box was grabbed at this step, 0..2, or -1 for none. */
  pick = -1,
): number {
  return (
    (movement.forward ? BUTTON.forward : 0) |
    (movement.back ? BUTTON.back : 0) |
    (movement.left ? BUTTON.left : 0) |
    (movement.right ? BUTTON.right : 0) |
    (movement.jump ? BUTTON.jump : 0) |
    (action ? BUTTON.action : 0) |
    (sip ? BUTTON.sip : 0) |
    buttonForPick(pick)
  );
}

/** The bit for one box, or 0 for anything that is not one. */
export function buttonForPick(slot: number): number {
  switch (slot) {
    case 0:
      return BUTTON.pick1;
    case 1:
      return BUTTON.pick2;
    case 2:
      return BUTTON.pick3;
    default:
      return 0;
  }
}

/**
 * Which box a frame grabbed at, 0..2, or -1 for none.
 *
 * The lowest bit wins if a frame somehow carries two. A keyboard cannot
 * produce that in one 1/120th of a second, but a hand-built replay can, and
 * every input the verifier accepts has to have exactly one meaning.
 */
export function pickFromButtons(buttons: number): number {
  if ((buttons & BUTTON.pick1) !== 0) return 0;
  if ((buttons & BUTTON.pick2) !== 0) return 1;
  if ((buttons & BUTTON.pick3) !== 0) return 2;
  return -1;
}

export function sameInput(a: StepInput, b: StepInput): boolean {
  return a.buttons === b.buttons && a.yawQ === b.yawQ && a.pitchQ === b.pitchQ;
}
